import React, { useEffect, useContext, useState } from 'react';
import { ReactComponent as CrossIcon } from 'assets/icon/x-circle.svg';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import DarkButton from 'components/common/buttons/dark-btn';
import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { appointmentStatusEnum, initModal } from 'helpers/utils';
import { setModal } from 'store/features/modelSlice';
import { AppContext } from 'context/app-context';
import {
  IConfirmWorkshopAppointmentRequest,
  confirmWorkshopAppointment,
  getWorkshopAppointmentAvailableSlots,
  getWorkshopAppointmentList,
} from 'store/features/workshopAppointmentsSlice';
import moment from 'moment';
import toast from 'react-hot-toast';
import { getNotifications, readNotification } from 'store/features/notificationSlice';
import { useTranslation } from 'react-i18next';

export interface IAvailableAppointmentsType {
  appointment_id: string;
  date: string;
  is_booked: boolean;
  organization_id: string;
  parking_id: string;
  time: string;
  time_division: string;
  _id: string;
}

const AppointmentConfirmation: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const {
    modal: {
      content: { title, callBackFun },
    },
  } = useAppSelector(state => state.modal);
  const { selectedNotification } = useAppSelector(state => state.notifications);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<{
    [date: string]: IAvailableAppointmentsType[];
  } | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<IAvailableAppointmentsType | null>(null);
  const { setLoader } = useContext(AppContext);

  const handleSubmit = async () => {
    try {
      if (!selectedSlot) {
        toast.error(t('workshopAppointment.errorTimeslotToastMessage'));
        return;
      }
      setLoader(true);
      const request: IConfirmWorkshopAppointmentRequest = {
        slot_id: selectedSlot?._id || '',
        appointment_id: selectedNotification?.data?.appointment_id || '',
        status: appointmentStatusEnum?.SCHEDULED,
      };
      dispatch(confirmWorkshopAppointment({ request })).then(() => {
        const promises = [
          dispatch(readNotification({ notification_id: selectedNotification?._id || '' })),
          dispatch(getNotifications({ params: { type: 'notification' } })),
          dispatch(getWorkshopAppointmentList({})),
        ];
        Promise.all(promises).then(() => {
          if (callBackFun) {
            callBackFun();
          }
          toast.success(t('workshopAppointment.successAppointmentBookToastMessage'));
          dispatch(setModal(initModal));
          setLoader(false);
        });
      }).catch((err) => {
        console.log({ err })
      })
    } catch (error: any) {
      toast.error(error.message);
    }
    finally {
      setLoader(false);
    }
  };
  const getAppointmentDataGroupBy = (appointments: IAvailableAppointmentsType[]) => {
    const groupedAppointments: { [date: string]: IAvailableAppointmentsType[] } =
      appointments.reduce(
        (result: { [date: string]: IAvailableAppointmentsType[] }, appointment) => {
          const date = appointment.date;
          if (!result[date]) {
            result[date] = [];
          }
          result[date].push(appointment);
          return result;
        },
        {},
      );
    return Promise.resolve(groupedAppointments);
  };
  const fetchAvailableSlotsList = async () => {
    try {
      setLoader(true);
      dispatch(
        getWorkshopAppointmentAvailableSlots({
          appointment_id: selectedNotification?.data?.appointment_id || '',
        }),
      ).then(async (response: IAvailableAppointmentsType[]) => {
        const groupData = await getAppointmentDataGroupBy(response);
        setAvailableTimeSlots(groupData);
        setLoader(false);
      });
    } catch (error) {
      setLoader(false);
    }
  };
  useEffect(() => {
    fetchAvailableSlotsList();
  }, []);
  return (
    <div className="flex flex-wrap max-w-[450px] w-[450px] gap-y-3 overflow-hidden">
      <div className="flex items-center w-full">
        <div className="flex items-center flex-1">
          <label className="pl-2 text-xl font-semibold text-black2">
            {title || t('workshopAppointment.bookAppointment')}
          </label>
        </div>
        <div
          className="flex items-center justify-center cursor-pointer"
          onClick={() => dispatch(setModal(initModal))}
        >
          <CrossIcon height={32} width={32} />
        </div>
      </div>
      <div className="flex flex-wrap w-full overflow-y-auto max-h-[calc(100vh-250px)] gap-y-2 bg-grey4 rounded-4 p-4">
        {availableTimeSlots &&
          Object.entries(availableTimeSlots).map(([date, appointmentData]) => (
            <div className="flex flex-wrap gap-y-1 items-center w-full">
              <label className="font-bold text-base text-black2 w-full">
                {moment(date).format('MMMM DD, YYYY')}
              </label>
              <div className="flex flex-wrap w-full">
                {[...appointmentData].map(data => (
                  <div className="w-1/6  py-1 flex items-center">
                    <div
                      className={`flex items-center justify-center px-3 py-2 rounded-full border-active border-1 w-30 cursor-pointer ${data?._id === selectedSlot?._id ? 'bg-primary' : 'bg-white'
                        }`}
                      onClick={() => setSelectedSlot(data)}
                    >
                      <label
                        className={`flex justify-center items-center text-center text-sm font-semibold cursor-pointer ${data?._id === selectedSlot?._id ? 'text-white' : 'text-primary'
                          }`}
                      >
                        {`${data?.time}`}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
      <div className="flex items-center justify-center w-full gap-x-4 mt-4">
        <PrimaryDarkButton
          label={t('workshopAppointment.bookNowBtn')}
          onClick={() => handleSubmit()}
          width="w-1/2"
        />
        <DarkButton
          label={t('CANCEL')}
          width="w-1/2"
          onClick={() => dispatch(setModal(initModal))}
        />
      </div>
    </div>
  );
};

export default AppointmentConfirmation;
